import {
  Inline,
  Button,
  Box,
  IconArrowRight,
  IconArrowLeft,
  useResponsiveValue,
  Stack,
  useDialogController,
  useToast
} from '@machobear/design-system'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router'
import { assoc, pipe, pick } from 'ramda'
import ExitConfirmationModal from '@/components/exit-confirmation-modal'
import { useFormContext } from './form-context'
import { useUserIdState } from './hook/useUserIdState'
import { FORM_STEP_NUMBER as FORM } from '.'

type Form = { [key: number]: string }
type FormStepNumber = Form | { [key: number]: string }
type AssocFunction = (
  key: string,
  value: string
) => (obj: { [key: string]: string }) => { [key: string]: string }

const FormFooter = ({
  onSave,
  onSubmit,
  saveCta = 'Save',
  submitCta = 'Next',
  isLoading,
  isSubmitting,
  showPayment = true
}: {
  saveCta?: string
  submitCta?: string
  onSave?: () => void
  onSubmit?: () => void
  isLoading?: boolean
  isSubmitting?: boolean
  showPayment?: boolean
}) => {
  const { t } = useTranslation()
  const { dirty } = useFormContext()
  const { formType } = useParams<{ formType: string }>()
  const showToast = useToast()
  const { isUserIdUnverified, isUserIdVerified } = useUserIdState()
  const assocFn: AssocFunction = assoc

  const FORM_STEP_NUMBER: FormStepNumber = !showPayment
    ? pipe(pick(['1', '2']), assocFn('3', FORM['4']))(FORM)
    : FORM
  const step = Object.entries(FORM_STEP_NUMBER).find(([key, val]) => val === formType) || [
    '1',
    'credit-check'
  ]

  const activeStep = parseInt(step[0])
  const history = useHistory()

  const { visible, showModal, closeModal } = useDialogController()
  const isConfirmationPage = formType === 'confirmation'

  const showSaveBtn = !!onSave

  const showWanirng = () => {
    return showToast({
      message: isUserIdUnverified
        ? t('l.standalone_trustscore.form_verification_required_title', 'ID Verification Needed')
        : t(
            'l.listing.application.secure_docs.id_verification_required.heading_alt',
            'ID Verification in progress'
          ),
      description: isUserIdUnverified
        ? t(
            'l.standalone_trustscore.form_verification_required_description',
            'We require additional information to verify your account before you can continue using the Trust Score.'
          )
        : t(
            'l.standalone_trustscore.form_verification_pending_warning_description_alt',
            'We’ll send an email when verification is complete. Once verified, please try again.'
          ),
      tone: 'critical'
    })
  }

  const handleBack = () => {
    const previousStep = activeStep - 1

    if (previousStep === 0) return history.push('/trust-score')
    const previousLink = FORM_STEP_NUMBER[previousStep]
    history.push(previousLink)
  }

  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false
  })

  const handleOnSave = () => {
    if (isUserIdUnverified) return showWanirng()
    onSave && onSave()
  }
  const handleOnSubmit = () => {
    if (!isUserIdVerified) return showWanirng()
    onSubmit && onSubmit()
  }

  const handleClickBack = () => {
    if (dirty) return showModal()
    handleBack()
  }

  const handleModalConfirm = () => {
    handleBack()
    closeModal()
  }

  return (
    <>
      <ExitConfirmationModal
        visible={visible}
        closeModal={closeModal}
        handleModalConfirm={handleModalConfirm}
      />
      {isMobile ? (
        <Box padding="gutter">
          <Stack space="medium" align="center">
            {isConfirmationPage ? (
              <Button
                id="credit-check-unlock"
                type={'button'}
                onClick={handleOnSubmit}
                loading={isSubmitting}
                disabled={isLoading}
              >
                {isSubmitting ? 'Submitting' : 'Submit'}
              </Button>
            ) : (
              <Button
                id="credit-check-update"
                iconPosition="trailing"
                type="submit"
                loading={isSubmitting}
                disabled={isLoading}
              >
                Next
              </Button>
            )}
            {showSaveBtn && (
              <Button
                id="credit-check-save"
                onClick={handleOnSave}
                variant="transparent"
                type="button"
                disabled={isLoading || isSubmitting}
              >
                {saveCta}
              </Button>
            )}
          </Stack>
        </Box>
      ) : (
        <Box display={'flex'} justifyContent="spaceBetween">
          <Inline space={'none'}>
            <Button
              variant="transparent"
              icon={<IconArrowLeft />}
              onClick={handleClickBack}
              disabled={isLoading || isSubmitting}
            >
              {t('common.back', 'Back')}
            </Button>
          </Inline>
          <Inline space="gutter" align="right">
            {showSaveBtn && (
              <Button
                id="credit-check-save"
                onClick={handleOnSave}
                variant="transparent"
                type="button"
                disabled={isLoading || isSubmitting}
              >
                {saveCta}
              </Button>
            )}
            {isConfirmationPage ? (
              <Button
                id="credit-check-unlock"
                type="button"
                onClick={handleOnSubmit}
                loading={isSubmitting}
                disabled={isLoading}
              >
                {isSubmitting
                  ? t('common.submitting2', 'Submitting')
                  : t('common.submit', 'Submit')}
              </Button>
            ) : (
              <Button
                id="credit-check-update"
                iconPosition="trailing"
                icon={submitCta === 'Next' ? <IconArrowRight /> : <></>}
                type="submit"
                loading={isSubmitting}
                disabled={isLoading}
              >
                {t('common.next', 'Next')}
              </Button>
            )}
          </Inline>
        </Box>
      )}
    </>
  )
}

export default FormFooter
